import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { PORTAL_URL } from "@/config/intake";

const CANONICAL = "https://olivehealth.care/blog/glp-1-dosing-schedules";
const TITLE = "GLP-1 Dosing Schedule: Semaglutide & Tirzepatide Guide";
const DESCRIPTION =
  "Clinician-reviewed GLP-1 dosing guide: Semaglutide (Wegovy/Ozempic) and Tirzepatide (Zepbound/Mounjaro) titration, timing, missed doses, side effects.";
const PUBLISHED = "2026-06-07";

const SEMA_SCHEDULE = [
  { week: "Weeks 1–4", dose: "0.25 mg once weekly", note: "Starter dose. Not for weight-loss effect — purpose is to acclimate the gut." },
  { week: "Weeks 5–8", dose: "0.5 mg once weekly", note: "First true therapeutic step. Many feel appetite changes here." },
  { week: "Weeks 9–12", dose: "1.0 mg once weekly", note: "Hold longer if GI side effects are still settling." },
  { week: "Weeks 13–16", dose: "1.7 mg once weekly", note: "Optional plateau dose for patients sensitive to escalation." },
  { week: "Week 17+", dose: "2.4 mg once weekly (maintenance)", note: "STEP-1 trial endpoint dose. Mean ~14.9% TBWL at 68 weeks." },
];

const TIRZ_SCHEDULE = [
  { week: "Weeks 1–4", dose: "2.5 mg once weekly", note: "Initiation. Not intended for weight-loss effect on its own." },
  { week: "Weeks 5–8", dose: "5 mg once weekly", note: "First maintenance-eligible dose." },
  { week: "Weeks 9–12", dose: "7.5 mg once weekly", note: "Step only if tolerating well and response is sub-target." },
  { week: "Weeks 13–16", dose: "10 mg once weekly", note: "Common long-term maintenance dose." },
  { week: "Weeks 17–20", dose: "12.5 mg once weekly", note: "Optional step toward higher response." },
  { week: "Week 21+", dose: "15 mg once weekly (maintenance)", note: "SURMOUNT-1 endpoint dose. Mean ~20.9% TBWL at 72 weeks." },
];

export default function BlogGlp1DosingSchedules() {
  useEffect(() => {
    document.title = TITLE;
    const setMeta = (selector: string, attr: string, key: string, content: string) => {
      let el = document.querySelector(selector) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(`meta[name="description"]`, "name", "description", DESCRIPTION);
    setMeta(`meta[property="og:title"]`, "property", "og:title", TITLE);
    setMeta(`meta[property="og:description"]`, "property", "og:description", DESCRIPTION);
    setMeta(`meta[property="og:url"]`, "property", "og:url", CANONICAL);
    setMeta(`meta[property="og:type"]`, "property", "og:type", "article");
    setMeta(
      `meta[property="og:image"]`,
      "property",
      "og:image",
      "https://olivehealth.care/og/blog-glp-1-dosing-schedules.png",
    );
    setMeta(`meta[name="twitter:title"]`, "name", "twitter:title", TITLE);
    setMeta(`meta[name="twitter:description"]`, "name", "twitter:description", DESCRIPTION);
    setMeta(`meta[name="twitter:card"]`, "name", "twitter:card", "summary_large_image");

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", CANONICAL);

    const ldId = "ld-blog-glp1-dosing";
    document.getElementById(ldId)?.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = ldId;
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MedicalWebPage",
      headline: TITLE,
      description: DESCRIPTION,
      url: CANONICAL,
      datePublished: PUBLISHED,
      dateModified: PUBLISHED,
      author: { "@type": "Organization", name: "Olive Health" },
      publisher: {
        "@type": "MedicalBusiness",
        name: "Olive Health",
        url: "https://olivehealth.care",
      },
      mainEntityOfPage: CANONICAL,
      about: [
        { "@type": "Drug", name: "Semaglutide" },
        { "@type": "Drug", name: "Tirzepatide" },
      ],
    });
    document.head.appendChild(s);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main>
        <article className="container-wide pt-28 pb-16 md:pt-36 md:pb-24">
          <motion.header
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-[0.2em] text-foreground/60 mb-4">
              <Link to="/" className="hover:text-foreground">Olive Health</Link>
              <span className="mx-2">·</span>
              <span>Blog</span>
            </nav>
            <h1 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight">
              GLP-1 dosing schedules: Semaglutide & Tirzepatide titration guide
            </h1>
            <p className="mt-5 text-lg text-foreground/70 max-w-2xl leading-relaxed">
              A clinician-reviewed reference for the standard titration ladders, how to
              handle missed doses, and what tolerability looks like at each step. For
              education only — your prescriber tailors the plan to you.
            </p>
            <p className="mt-3 text-sm text-foreground/50">
              Reviewed June 7, 2026 · ~7 min read
            </p>
          </motion.header>

          <div className="prose-custom max-w-3xl mt-12 space-y-10">
            <section>
              <h2 className="font-display text-2xl md:text-3xl mb-4">Why titration matters</h2>
              <p className="text-foreground/80 leading-relaxed">
                GLP-1 receptor agonists slow gastric emptying and dampen appetite signaling.
                Starting at the eventual maintenance dose would put most patients out of
                commission with nausea for a week. The dose is therefore stepped up over
                roughly 16 to 20 weeks so the gut and brain can adapt. Skipping steps is a
                leading cause of treatment drop-off — patience at low doses pays back in
                adherence over the months that matter.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl md:text-3xl mb-4">
                Semaglutide (Wegovy, Ozempic) — standard schedule
              </h2>
              <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-foreground/5">
                    <tr>
                      <th className="px-4 py-3 font-medium">When</th>
                      <th className="px-4 py-3 font-medium">Dose</th>
                      <th className="px-4 py-3 font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SEMA_SCHEDULE.map((row) => (
                      <tr key={row.week} className="border-t border-border/60">
                        <td className="px-4 py-3 align-top whitespace-nowrap">{row.week}</td>
                        <td className="px-4 py-3 align-top font-medium">{row.dose}</td>
                        <td className="px-4 py-3 align-top text-foreground/70">{row.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="font-display text-2xl md:text-3xl mb-4">
                Tirzepatide (Zepbound, Mounjaro) — standard schedule
              </h2>
              <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
                <table className="w-full text-left text-sm">
                  <thead className="bg-foreground/5">
                    <tr>
                      <th className="px-4 py-3 font-medium">When</th>
                      <th className="px-4 py-3 font-medium">Dose</th>
                      <th className="px-4 py-3 font-medium">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TIRZ_SCHEDULE.map((row) => (
                      <tr key={row.week} className="border-t border-border/60">
                        <td className="px-4 py-3 align-top whitespace-nowrap">{row.week}</td>
                        <td className="px-4 py-3 align-top font-medium">{row.dose}</td>
                        <td className="px-4 py-3 align-top text-foreground/70">{row.note}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="font-display text-2xl md:text-3xl mb-4">Timing of injections</h2>
              <ul className="space-y-3 text-foreground/80 leading-relaxed list-disc pl-6">
                <li>Both medications are once-weekly subcutaneous injections — abdomen, thigh, or upper arm.</li>
                <li>Pick a day of the week and stick to it. The day can be changed as long as the previous dose was at least 48 hours earlier.</li>
                <li>Time of day does not matter. Many patients prefer an evening dose so peak nausea passes during sleep.</li>
                <li>Rotate injection sites to reduce local irritation.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl md:text-3xl mb-4">Missed doses</h2>
              <ul className="space-y-3 text-foreground/80 leading-relaxed list-disc pl-6">
                <li>
                  <strong>Less than 5 days late:</strong> take the missed dose as soon as you
                  remember, then resume the normal weekly schedule.
                </li>
                <li>
                  <strong>More than 5 days late:</strong> skip the missed dose and take the
                  next one on the regular day.
                </li>
                <li>
                  <strong>Two or more weeks off:</strong> contact your clinician. You may need
                  to step back down a dose to avoid a rebound in side effects.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="font-display text-2xl md:text-3xl mb-4">Managing side effects</h2>
              <p className="text-foreground/80 leading-relaxed">
                Nausea, reflux, and constipation are most common in the first two weeks of any
                new dose. They typically fade as the body adapts. Practical levers: smaller
                meals, lower fat, more water and fiber, walking after meals, and short courses
                of OTC remedies (antacids, simethicone, polyethylene glycol). If symptoms are
                severe or persistent, holding at the current dose for an extra month is
                completely reasonable — outcomes are similar at slightly slower titrations.
              </p>
            </section>

            <section>
              <h2 className="font-display text-2xl md:text-3xl mb-4">When to call your clinician</h2>
              <ul className="space-y-3 text-foreground/80 leading-relaxed list-disc pl-6">
                <li>Severe abdominal pain radiating to the back (possible pancreatitis).</li>
                <li>Persistent vomiting or inability to keep fluids down for 24+ hours.</li>
                <li>Signs of gallbladder issues — right-upper-quadrant pain, fever, jaundice.</li>
                <li>Pregnancy or planning pregnancy — GLP-1s should be stopped at least 2 months before conception.</li>
              </ul>
            </section>

            <section className="rounded-2xl border border-border/60 bg-card p-6 md:p-8">
              <h2 className="font-display text-2xl mb-3">Estimate your trajectory</h2>
              <p className="text-foreground/70 leading-relaxed mb-5">
                Use our free GLP-1 calculator to project your weight loss on Semaglutide or
                Tirzepatide using STEP-1 and SURMOUNT-1 trial data.
              </p>
              <Link
                to="/tools/glp-1-calculator"
                className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:opacity-80"
              >
                Open the GLP-1 calculator <ArrowRight className="w-4 h-4" />
              </Link>
            </section>

            <section className="rounded-2xl bg-foreground text-background p-6 md:p-8">
              <h2 className="font-display text-2xl mb-3">Get a personalized plan</h2>
              <p className="text-background/80 leading-relaxed mb-5">
                Olive Health pairs you with a US-licensed clinician who chooses the right
                medication, dose, and titration cadence for your goals and tolerability.
              </p>
              <a
                href={PORTAL_URL}
                className="inline-flex items-center gap-2 rounded-full bg-background text-foreground px-5 py-2.5 text-sm font-medium hover:opacity-90"
              >
                Start your intake <ArrowRight className="w-4 h-4" />
              </a>
            </section>

            <p className="text-xs text-foreground/50 leading-relaxed">
              Educational content, not medical advice. Dosing references reflect FDA-approved
              labeling for Wegovy, Ozempic, Zepbound, and Mounjaro as of the review date.
              Always follow the prescribing information from your clinician and pharmacy.
            </p>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
